/**
 * Blasts. All of them carry across the whole map (`maxDistance`), arriving as a dull thump
 * once the distance filter has taken the top off - which is the point: a charge going off
 * 150 m north tells you your bomb worked without your seeing it.
 */

import { PRIORITY, type SfxDef } from './types';

export const EXPLOSION_SFX = {
  explosionSmall: {
    variants: 3, priority: PRIORITY.action, gain: 0.95, maxDistance: 220, rateSpread: 0.05,
    patch: {
      layers: [
        { wave: 'sine', freq: 98, freqEnd: 28, sweep: 0.5, decay: 0.5, gain: 1 },
        { wave: 'noise', decay: 0.9, curve: 2.2, gain: 1, filter: { type: 'lp', cutoff: 3600, cutoffEnd: 170 } },
        { wave: 'noise', decay: 0.05, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 1200 } },
        // Dirt and fragments coming back down.
        { wave: 'noise', at: 0.16, attack: 0.05, decay: 0.7, gain: 0.2, tremHz: 23, tremDepth: 0.9,
          filter: { type: 'bp', cutoff: 2300, cutoffEnd: 1200, q: 1 } },
      ],
      jitter: { pitch: 0.08, time: 0.1, gain: 0.1 }, drive: 3,
    },
  },
  explosionLarge: {
    variants: 3, priority: PRIORITY.critical, gain: 1, maxDistance: 320, rateSpread: 0.04,
    patch: {
      layers: [
        { wave: 'sine', freq: 72, freqEnd: 20, sweep: 0.5, decay: 0.95, gain: 1 },
        { wave: 'saw', freq: 46, freqEnd: 22, decay: 1.2, gain: 0.6, drive: 3, filter: { type: 'lp', cutoff: 210 } },
        { wave: 'noise', decay: 1.9, curve: 2.4, gain: 1, filter: { type: 'lp', cutoff: 2900, cutoffEnd: 90 } },
        { wave: 'noise', decay: 0.06, curve: 3, gain: 0.8, filter: { type: 'hp', cutoff: 900 } },
        { wave: 'noise', at: 0.3, attack: 0.08, decay: 1.4, gain: 0.2, tremHz: 19, tremDepth: 0.9,
          filter: { type: 'bp', cutoff: 1900, cutoffEnd: 800, q: 0.9 } },
      ],
      jitter: { pitch: 0.06, time: 0.08, gain: 0.1 }, drive: 4, echo: { time: 0.19, gain: 0.35, count: 2 },
    },
  },
  /** Fuel and ammunition going up after the first blast: a fizz, a bang, rounds popping off. */
  cookOff: {
    variants: 3, priority: PRIORITY.action, gain: 0.95, maxDistance: 260, rateSpread: 0.06,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.05, decay: 0.06, gain: 0.35, filter: { type: 'hp', cutoff: 4200 } },
        { wave: 'sine', at: 0.1, freq: 120, freqEnd: 30, sweep: 0.5, decay: 0.6, gain: 1 },
        { wave: 'noise', at: 0.1, decay: 1.2, curve: 2.2, gain: 1,
          filter: { type: 'lp', cutoff: 5200, cutoffEnd: 200 } },
        { wave: 'noise', at: 0.1, decay: 0.05, curve: 3, gain: 0.8, filter: { type: 'hp', cutoff: 1500 } },
        { wave: 'noise', at: 0.35, attack: 0.02, decay: 1.1, gain: 0.3, tremHz: 11, tremDepth: 1, tremSquare: true,
          filter: { type: 'bp', cutoff: 2600, q: 1.4 } },
      ],
      jitter: { pitch: 0.1, time: 0.12, gain: 0.15 }, drive: 3.2,
    },
  },
  /** The click under the boot, a heartbeat of nothing, then the bang. */
  mine: {
    variants: 2, priority: PRIORITY.critical, gain: 1, maxDistance: 200,
    patch: {
      layers: [
        { wave: 'sine', freq: 2600, fmRatio: 1.9, fmIndex: 2, fmDecay: 0.006, decay: 0.025, curve: 3, gain: 0.5 },
        { wave: 'sine', at: 0.17, freq: 110, freqEnd: 30, sweep: 0.45, decay: 0.45, gain: 1 },
        { wave: 'noise', at: 0.17, decay: 0.75, curve: 2.4, gain: 1,
          filter: { type: 'lp', cutoff: 4500, cutoffEnd: 200 } },
        { wave: 'noise', at: 0.17, decay: 0.05, curve: 3, gain: 0.9, filter: { type: 'hp', cutoff: 1400 } },
        { wave: 'noise', at: 0.3, attack: 0.04, decay: 0.6, gain: 0.25, tremHz: 27, tremDepth: 0.9,
          filter: { type: 'bp', cutoff: 2000, q: 1 } },
      ],
      jitter: { pitch: 0.06, time: 0.06 }, drive: 3.4,
    },
  },
  /** A structure coming apart, at the prop's own position. */
  debris: {
    variants: 3, priority: PRIORITY.detail, gain: 0.55, maxDistance: 120, rateSpread: 0.1, minInterval: 0.05,
    patch: {
      layers: [
        { wave: 'noise', attack: 0.05, decay: 1.1, gain: 1, tremHz: 17, tremDepth: 0.75,
          filter: { type: 'bp', cutoff: 650, cutoffEnd: 240, q: 0.8 } },
        { wave: 'brown', attack: 0.03, decay: 1.2, gain: 0.8, filter: { type: 'lp', cutoff: 220 } },
        { wave: 'sine', at: 0.12, freq: 900, fmRatio: 1.43, fmIndex: 3, fmDecay: 0.03, decay: 0.08, curve: 3,
          gain: 0.4 },
        { wave: 'sine', at: 0.41, freq: 640, fmRatio: 1.43, fmIndex: 3, fmDecay: 0.03, decay: 0.1, curve: 3,
          gain: 0.35 },
        { wave: 'sine', at: 0.66, freq: 1100, fmRatio: 2.1, fmIndex: 2, fmDecay: 0.02, decay: 0.06, curve: 3,
          gain: 0.25 },
      ],
      jitter: { pitch: 0.12, time: 0.15, gain: 0.15 },
    },
  },
} satisfies Record<string, SfxDef>;
